import { supabaseClient } from '@plataforma/supabase-client';

export const TAMANO_PAGINA = 20;

export function obtenerDashboardResumen() {
  return supabaseClient().rpc('rpc_super_admin_dashboard_resumen');
}

export function obtenerIngresosResumen({ desde = null, hasta = null } = {}) {
  return supabaseClient().rpc('rpc_super_admin_ingresos_resumen', {
    p_desde: desde,
    p_hasta: hasta,
  });
}

/**
 * Salud del outbox de Vimos Digital (solo lectura, exclusivo super_admin).
 * Devuelve conteos por estado, métricas de flujo (pendientes listas,
 * sending atascadas, enviadas 24h), banderas de anomalías y una muestra
 * acotada de los eventos a revisar.
 */
export function obtenerVimosOutboxResumen() {
  return supabaseClient().rpc('rpc_super_admin_vimos_outbox_resumen');
}

export async function listarEmpresasBackoffice(filtros = {}) {
  const {
    busqueda = '',
    estado = '',
    planId = '',
    trialEstado = '',
    pagina = 1,
    orden = 'creado_en',
    direccion = 'desc',
  } = filtros;

  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('empresa')
    .select(
      `
      id,
      nombre,
      slug,
      subdominio,
      estado,
      moneda,
      creado_en,
      trial_inicia_en,
      trial_fin_en,
      trial_estado,
      plan!empresa_plan_id_fkey ( id, nombre, precio_mensual ),
      suscripcion ( id, estado, inicia_en, fin_en, plan_id )
    `,
      { count: 'exact' },
    )
    .is('eliminado_en', null);

  if (busqueda.trim()) {
    const texto = busqueda.trim();
    consulta = consulta.or(
      `nombre.ilike.%${texto}%,slug.ilike.%${texto}%,subdominio.ilike.%${texto}%`,
    );
  }

  // Los 'borrador' solo se muestran cuando se filtra explícitamente por ellos;
  // por defecto quedan fuera de las listas comerciales normales (una cuenta
  // abandonada no debe aparecer como tienda activa).
  if (estado) consulta = consulta.eq('estado', estado);
  else consulta = consulta.neq('estado', 'borrador');
  if (planId) consulta = consulta.eq('plan_id', planId);
  if (trialEstado) consulta = consulta.eq('trial_estado', trialEstado);

  return consulta.order(orden, { ascending: direccion === 'asc' }).range(desde, hasta);
}

export async function obtenerEmpresaDetalle(empresaId) {
  const cliente = supabaseClient();

  const [
    empresa,
    usuarios,
    suscripciones,
    contrataciones,
    pagos,
    dominios,
    publicaciones,
    auditoria,
  ] = await Promise.all([
    cliente
      .from('empresa')
      .select(
        `
          id,
          nombre,
          slug,
          subdominio,
          estado,
          moneda,
          zona_horaria,
          creado_en,
          actualizado_en,
          trial_inicia_en,
          trial_fin_en,
          trial_estado,
          plan!empresa_plan_id_fkey ( id, nombre, precio_mensual, limite_productos, limite_imagenes, limite_usuarios_admin )
        `,
      )
      .eq('id', empresaId)
      .is('eliminado_en', null)
      .single(),
    cliente
      .from('usuario_plataforma')
      .select('id, rol, nombre_completo, estado, creado_en')
      .eq('empresa_id', empresaId)
      .order('creado_en', { ascending: false }),
    cliente
      .from('suscripcion')
      .select(
        'id, estado, inicia_en, fin_en, creado_en, actualizado_en, plan ( id, nombre, precio_mensual )',
      )
      .eq('empresa_id', empresaId)
      .order('creado_en', { ascending: false }),
    cliente
      .from('solicitud_contratacion')
      .select(
        'id, estado, monto, metodo_pago, evidencia_nombre, motivo_rechazo, creado_en, revisado_en, plan ( id, nombre, precio_mensual )',
      )
      .eq('empresa_id', empresaId)
      .order('creado_en', { ascending: false })
      .limit(20),
    cliente
      .from('pago')
      .select('id, periodo, monto, estado, metodo, fecha_pago, comprobante_url, creado_en')
      .eq('empresa_id', empresaId)
      .order('periodo', { ascending: false })
      .limit(20),
    cliente
      .from('dominio')
      .select('id, tipo, valor, estado_dns, es_principal, creado_en')
      .eq('empresa_id', empresaId)
      .order('creado_en', { ascending: false }),
    cliente
      .from('publicacion')
      .select(
        'id, estado, iniciado_en, finalizado_en, detalle_error, archivos_generados, duracion_ms',
      )
      .eq('empresa_id', empresaId)
      .order('iniciado_en', { ascending: false })
      .limit(20),
    cliente
      .from('log_auditoria')
      .select('id, usuario_id, accion, entidad, entidad_id, detalle, creado_en')
      .eq('empresa_id', empresaId)
      .order('creado_en', { ascending: false })
      .limit(20),
  ]);

  const error = [
    empresa,
    usuarios,
    suscripciones,
    contrataciones,
    pagos,
    dominios,
    publicaciones,
    auditoria,
  ].find((r) => r.error)?.error;

  return {
    data: error
      ? null
      : {
          empresa: empresa.data,
          usuarios: usuarios.data ?? [],
          suscripciones: suscripciones.data ?? [],
          contrataciones: contrataciones.data ?? [],
          pagos: pagos.data ?? [],
          dominios: dominios.data ?? [],
          publicaciones: publicaciones.data ?? [],
          auditoria: auditoria.data ?? [],
        },
    error,
  };
}

export async function listarPlanesBackoffice() {
  const cliente = supabaseClient();
  const [planes, empresas] = await Promise.all([
    cliente
      .from('plan')
      .select(
        'id, nombre, precio_mensual, limite_productos, limite_imagenes, limite_usuarios_admin, caracteristicas, estado, creado_en, actualizado_en',
      )
      .order('precio_mensual', { ascending: true }),
    cliente.from('empresa').select('plan_id').is('eliminado_en', null),
  ]);

  if (planes.error) return { data: null, error: planes.error };
  if (empresas.error) return { data: null, error: empresas.error };

  const conteos = new Map();
  for (const empresa of empresas.data ?? []) {
    conteos.set(empresa.plan_id, (conteos.get(empresa.plan_id) ?? 0) + 1);
  }

  return {
    data: (planes.data ?? []).map((plan) => ({
      ...plan,
      empresas_usando: conteos.get(plan.id) ?? 0,
    })),
    error: null,
  };
}

export function guardarPlan(datos) {
  return supabaseClient().rpc('rpc_super_admin_guardar_plan', {
    p_plan_id: datos.id ?? null,
    p_nombre: datos.nombre,
    p_precio_mensual: Number(datos.precio_mensual),
    p_estado: datos.estado,
    p_limite_productos: Number(datos.limite_productos),
    p_limite_imagenes: Number(datos.limite_imagenes),
    p_limite_usuarios_admin: Number(datos.limite_usuarios_admin),
    p_caracteristicas: datos.caracteristicas ?? {},
    p_motivo: datos.motivo,
  });
}

export function cambiarEstadoEmpresa(empresaId, nuevoEstado, motivo) {
  return supabaseClient().rpc('rpc_super_admin_cambiar_estado_empresa', {
    p_empresa_id: empresaId,
    p_nuevo_estado: nuevoEstado,
    p_motivo: motivo,
  });
}

export function cambiarPlanEmpresa(empresaId, nuevoPlanId, motivo) {
  return supabaseClient().rpc('rpc_super_admin_upgrade_gratuito', {
    p_empresa_id: empresaId,
    p_nuevo_plan_id: nuevoPlanId,
    p_motivo: motivo,
  });
}

export function extenderTrial(empresaId, dias, motivo) {
  return supabaseClient().rpc('rpc_super_admin_extender_trial', {
    p_empresa_id: empresaId,
    p_dias: Number(dias),
    p_motivo: motivo,
  });
}

export function actualizarUsuarioBackoffice(usuarioId, cambios) {
  return supabaseClient().rpc('rpc_super_admin_actualizar_usuario', {
    p_usuario_id: usuarioId,
    p_rol: cambios.rol,
    p_estado: cambios.estado,
    p_nombre_completo: cambios.nombre_completo,
    p_motivo: cambios.motivo,
  });
}

export async function listarSuscripcionesBackoffice(filtros = {}) {
  const { estado = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('suscripcion')
    .select(
      'id, estado, inicia_en, fin_en, creado_en, actualizado_en, empresa ( id, nombre, subdominio, trial_estado, periodo_activo_fin, periodo_servicio ( id, estado, periodo_inicio, periodo_fin ) ), plan ( id, nombre, precio_mensual )',
      { count: 'exact' },
    )
    .order('creado_en', { ascending: false });

  if (estado) consulta = consulta.eq('estado', estado);

  return consulta.range(desde, hasta);
}

export async function listarTrialsBackoffice(filtros = {}) {
  const { estado = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('empresa')
    .select(
      'id, nombre, subdominio, estado, trial_inicia_en, trial_fin_en, trial_estado, plan!empresa_plan_id_fkey ( id, nombre )',
      {
        count: 'exact',
      },
    )
    .is('eliminado_en', null)
    .order('trial_fin_en', { ascending: true, nullsFirst: false });

  if (estado) consulta = consulta.eq('trial_estado', estado);

  return consulta.range(desde, hasta);
}

export async function listarUsuariosBackoffice(filtros = {}) {
  const { busqueda = '', rol = '', estado = '', empresaId = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('usuario_plataforma')
    .select(
      'id, empresa_id, empresa_personal_id, rol, nombre_completo, estado, creado_en, empresa!usuario_plataforma_empresa_id_fkey ( id, nombre )',
      {
        count: 'exact',
      },
    )
    .order('creado_en', { ascending: false });

  if (busqueda.trim()) consulta = consulta.ilike('nombre_completo', `%${busqueda.trim()}%`);
  if (rol) consulta = consulta.eq('rol', rol);
  if (estado) consulta = consulta.eq('estado', estado);
  if (empresaId) consulta = consulta.eq('empresa_id', empresaId);

  return consulta.range(desde, hasta);
}

export async function listarPagosBackoffice(filtros = {}) {
  const { estado = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('pago')
    .select(
      'id, periodo, monto, estado, metodo, metodo_pago, fecha_pago, comprobante_url, periodo_inicio, periodo_fin, meses_solicitados, creado_por, creado_en, revisado_en, motivo_rechazo, empresa ( id, nombre ), plan ( id, nombre, precio_mensual )',
      { count: 'exact' },
    )
    .order('creado_en', { ascending: false });

  if (estado) consulta = consulta.eq('estado', estado);

  return consulta.range(desde, hasta);
}

export function aprobarPago(pagoId, motivo) {
  return supabaseClient().rpc('rpc_aprobar_pago', {
    p_pago_id: pagoId,
    p_motivo: motivo || null,
  });
}

export function rechazarPago(pagoId, motivo) {
  return supabaseClient().rpc('rpc_rechazar_pago', {
    p_pago_id: pagoId,
    p_motivo: motivo,
  });
}

/**
 * Obtiene la URL firmada del comprobante de un PAGO.
 * Solo super_admin (lo valida la Edge Function).
 * @param {string} pagoId
 * @returns {Promise<{data:{url_firmada:string,expira_en:number,mime:string|null}|null, error:object|null}>}
 */
export async function obtenerComprobantePago(pagoId) {
  const cliente = supabaseClient();

  let token = null;
  try {
    const session = await cliente.auth.getSession();
    token = session.data.session?.access_token ?? null;
  } catch {
    token = null;
  }

  if (!token) {
    return { data: null, error: { message: 'No autenticado.' } };
  }

  const baseUrl = cliente.supabaseUrl;
  const apiKey = cliente.supabaseKey ?? '';
  const url = `${baseUrl}/functions/v1/obtener_comprobante?tipo=pago&pago_id=${encodeURIComponent(pagoId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: apiKey,
    },
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    return {
      data: null,
      error: {
        status: response.status,
        code: body.code ?? response.statusText,
        message: body.error ?? response.statusText,
      },
    };
  }

  return { data: body, error: null };
}

export async function listarAuditoriaBackoffice(filtros = {}) {
  const { entidad = '', empresaId = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('log_auditoria')
    .select(
      'id, usuario_id, empresa_id, accion, entidad, entidad_id, detalle, creado_en, empresa ( id, nombre )',
      {
        count: 'exact',
      },
    )
    .order('creado_en', { ascending: false });

  if (entidad) consulta = consulta.eq('entidad', entidad);
  if (empresaId) consulta = consulta.eq('empresa_id', empresaId);

  return consulta.range(desde, hasta);
}

export function listarActividadBackoffice(filtros = {}) {
  return listarAuditoriaBackoffice(filtros);
}

export async function obtenerMiEmpresaSuperAdmin(usuarioId) {
  const { data: perfil, error } = await supabaseClient()
    .from('usuario_plataforma')
    .select('id, empresa_personal_id')
    .eq('id', usuarioId)
    .single();

  if (error || !perfil?.empresa_personal_id) {
    return { data: null, error };
  }

  return obtenerEmpresaDetalle(perfil.empresa_personal_id);
}

export function listarConfiguracionPlataforma() {
  return supabaseClient()
    .from('configuracion_plataforma')
    .select('clave, valor, descripcion, actualizado_en')
    .order('clave', { ascending: true });
}

// ----------------------------------------------------------------------------
// Métodos de pago y configuración de pagos (Super Admin)
// ----------------------------------------------------------------------------

/**
 * Lista todos los métodos de pago (activos e inactivos) ordenados.
 * Lectura directa: la RLS permite a super_admin leer todos.
 */
export function listarMetodosPago() {
  return supabaseClient()
    .from('metodos_pago')
    .select(
      'id, nombre, slug, tipo, numero, cci, qr_url, instrucciones, activo, orden, titular_personalizado, created_at, updated_at',
    )
    .order('orden', { ascending: true });
}

/**
 * Obtiene la configuración global de pagos (titular general). Una sola fila.
 */
export async function obtenerConfigPagos() {
  const { data, error } = await supabaseClient()
    .from('configuracion_pagos')
    .select('id, titular_general, updated_at')
    .eq('id', 1)
    .maybeSingle();

  return { data: data ?? null, error };
}

export function guardarConfigPagos(titularGeneral) {
  return supabaseClient().rpc('rpc_super_admin_guardar_config_pagos', {
    p_titular_general: titularGeneral,
  });
}

export function guardarMetodoPago(datos) {
  return supabaseClient().rpc('rpc_super_admin_guardar_metodo_pago', {
    p_id: datos.id ?? null,
    p_nombre: datos.nombre,
    p_slug: datos.slug,
    p_tipo: datos.tipo,
    p_numero: datos.numero || null,
    p_cci: datos.cci || null,
    p_qr_url: datos.qr_url || null,
    p_instrucciones: datos.instrucciones || null,
    p_activo: datos.activo,
    p_orden: Number(datos.orden ?? 0),
    p_titular_personalizado: datos.titular_personalizado || null,
  });
}

export function eliminarMetodoPago(id, motivo) {
  return supabaseClient().rpc('rpc_super_admin_eliminar_metodo_pago', {
    p_id: id,
    p_motivo: motivo,
  });
}

export function reordenarMetodosPago(ids) {
  return supabaseClient().rpc('rpc_super_admin_reordenar_metodos_pago', {
    p_ids: ids,
  });
}

/**
 * Sube/reemplaza/elimina el QR de un método de pago vía Edge Function
 * (service_role, super_admin únicamente). No se usa service_role_key en el
 * cliente.
 *
 * @param {string} accion - 'subir' | 'borrar'
 * @param {object} payload - { base64, mime, nombre, ruta_existente } para
 *   'subir'; { ruta } para 'borrar'.
 * @returns {Promise<{data: object|null, error: {message:string}|null}>}
 */
export async function gestionarQrPago(accion, payload = {}) {
  const { data, error } = await supabaseClient().functions.invoke('gestionar_qr_pago', {
    body: { accion, ...payload },
  });

  if (error) {
    let mensaje = error.message;
    try {
      const cuerpo = await error.context?.json?.();
      if (cuerpo?.error) {
        mensaje = cuerpo.error;
      }
    } catch {
      // Se conserva error.message tal cual.
    }
    return { data: null, error: { message: mensaje } };
  }

  return { data, error: null };
}

/**
 * Obtiene un resumen de la empresa para poblar el modal de confirmación de
 * eliminación (nombre, slug, n_usuarios, n_productos, plan, suscripción).
 * Solo lectura — no modifica datos.
 *
 * @param {string} empresaId
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export function preflightEliminarEmpresa(empresaId) {
  return supabaseClient().rpc('rpc_super_admin_preflight_eliminar_empresa', {
    p_empresa_id: empresaId,
  });
}

/**
 * Elimina una empresa y todos sus datos asociados de forma transaccional.
 * Usa RPC SECURITY DEFINER — nunca DELETE directo desde el cliente.
 * El motivo es obligatorio y queda registrado en log_auditoria.
 *
 * @param {string} empresaId
 * @param {string} motivo
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export function eliminarEmpresa(empresaId, motivo) {
  return supabaseClient().rpc('rpc_super_admin_eliminar_empresa', {
    p_empresa_id: empresaId,
    p_motivo: motivo,
  });
}

/**
 * Elimina un usuario de empresa de forma segura.
 * NO elimina la empresa del usuario.
 * Usa RPC SECURITY DEFINER — nunca DELETE directo desde el cliente.
 * El motivo es obligatorio y queda registrado en log_auditoria.
 *
 * @param {string} usuarioId
 * @param {string} motivo
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export function eliminarUsuario(usuarioId, motivo) {
  return supabaseClient().rpc('rpc_super_admin_eliminar_usuario', {
    p_usuario_id: usuarioId,
    p_motivo: motivo,
  });
}

// ----------------------------------------------------------------------------
// Beneficios
// ----------------------------------------------------------------------------
export function crearBeneficio(datos) {
  return supabaseClient().rpc('rpc_crear_beneficio', {
    p_empresa_id: datos.empresa_id,
    p_tipo: datos.tipo,
    p_descripcion: datos.descripcion,
    p_fecha_inicio: datos.fecha_inicio,
    p_fecha_fin: datos.fecha_fin,
    p_motivo: datos.motivo,
    p_estado: datos.estado ?? 'aprobado',
  });
}

export function aprobarBeneficio(beneficioId, motivo) {
  return supabaseClient().rpc('rpc_aprobar_beneficio', {
    p_beneficio_id: beneficioId,
    p_motivo: motivo || null,
  });
}

export function rechazarBeneficio(beneficioId, motivo) {
  return supabaseClient().rpc('rpc_rechazar_beneficio', {
    p_beneficio_id: beneficioId,
    p_motivo: motivo,
  });
}

export async function listarBeneficiosBackoffice(filtros = {}) {
  const { empresaId = '', estado = '', pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('beneficio')
    .select(
      'id, empresa_id, tipo, descripcion, fecha_inicio, fecha_fin, estado, motivo, motivo_rechazo, created_at, revisado_en, empresa ( id, nombre )',
      { count: 'exact' },
    )
    .order('created_at', { ascending: false });

  if (empresaId) consulta = consulta.eq('empresa_id', empresaId);
  if (estado) consulta = consulta.eq('estado', estado);

  return consulta.range(desde, hasta);
}

// ----------------------------------------------------------------------------
// Promociones
// ----------------------------------------------------------------------------
export function crearPromocion(datos) {
  return supabaseClient().rpc('rpc_crear_promocion', {
    p_nombre: datos.nombre,
    p_descripcion: datos.descripcion || null,
    p_tipo_descuento: datos.tipo_descuento,
    p_valor_descuento: Number(datos.valor_descuento),
    p_fecha_inicio: datos.fecha_inicio,
    p_fecha_fin: datos.fecha_fin,
    p_plan_ids: datos.plan_ids ?? null,
  });
}

export async function listarPromocionesBackoffice(filtros = {}) {
  const { soloActivas = false, pagina = 1 } = filtros;
  const desde = (pagina - 1) * TAMANO_PAGINA;
  const hasta = desde + TAMANO_PAGINA - 1;

  let consulta = supabaseClient()
    .from('promocion')
    .select(
      'id, nombre, descripcion, tipo_descuento, valor_descuento, fecha_inicio, fecha_fin, estado, created_at',
      { count: 'exact' },
    )
    .order('created_at', { ascending: false });

  if (soloActivas) consulta = consulta.eq('estado', 'activa');

  return consulta.range(desde, hasta);
}

/**
 * Segunda fase de la eliminación: borra los `auth.users` afectados.
 *
 * La RPC de negocio (`rpc_super_admin_eliminar_empresa` /
 * `rpc_super_admin_eliminar_usuario`) NO toca `auth.users` por seguridad:
 * lo delega a esta EDGE FUNCTION, que corre SOLO en el servidor con
 * SERVICE_ROLE. Nunca se expone la SERVICE_ROLE_KEY en el cliente.
 *
 * La Edge Function acepta ÚNICAMENTE `empresa_id` y obtiene los auth_user_id
 * desde la tabla `pendiente_eliminar_auth` (nunca desde el cliente). Es
 * idempotente: se puede reintentar con el mismo `empresaId` sin dejar
 * estado inconsistente.
 *
 * @param {{ empresaId?: string|null }} params
 * @returns {Promise<{data: {ok: boolean, empresa_id: string|null, eliminados: string[], fallidos: {id:string, error:string}[], total: number}|null, error: {message: string}|null}>}
 */
export async function eliminarAuthUsuarios({ empresaId = null } = {}) {
  const { data, error } = await supabaseClient().functions.invoke('eliminar-auth-usuarios', {
    body: { empresa_id: empresaId ?? null },
  });

  if (error) {
    let mensaje = error.message;
    try {
      const cuerpo = await error.context?.json?.();
      if (cuerpo?.error) {
        mensaje = cuerpo.detalles?.length
          ? `${cuerpo.error} ${cuerpo.detalles.join(' ')}`
          : cuerpo.error;
      }
    } catch {
      // Sin cuerpo JSON legible — se conserva error.message tal cual.
    }
    return { data: null, error: { message: mensaje } };
  }

  return { data, error: null };
}

/**
 * Tercera fase de la eliminación de empresa: desactiva la tienda pública.
 *
 * Escribe estado.json (disponible=false, 410) para que el Edge Gateway
 * rechace el tráfico de forma inmediata, y elimina los archivos publicados
 * de Storage (tiendas/{slug}/).
 *
 * Se invoca DESPUÉS de la RPC de eliminación y de eliminar-auth-usuarios.
 * Recibe el slug (no empresa_id) porque la empresa ya fue borrada de la BD.
 *
 * @param {{ slug: string }} params
 * @returns {Promise<{data: object|null, error: {message: string}|null}>}
 */
export async function desactivarTienda({ slug }) {
  const { data, error } = await supabaseClient().functions.invoke('desactivar-tienda', {
    body: { slug },
  });

  if (error) {
    let mensaje = error.message;
    try {
      const cuerpo = await error.context?.json?.();
      if (cuerpo?.error) {
        mensaje = cuerpo.detalles?.length
          ? `${cuerpo.error} ${cuerpo.detalles.join(' ')}`
          : cuerpo.error;
      }
    } catch {
      // Sin cuerpo JSON legible — se conserva error.message tal cual.
    }
    return { data: null, error: { message: mensaje } };
  }

  return { data, error: null };
}
